import queryString from "query-string";

import {
  BUSINESS,
  CREATE_BUSINESS_FROM_IMPORT,
  UPDATE_BUSINESS_ON_SUCCESS,
  TEMPORARY_BUSINESS_RESOLVE_STARTED,
  DELETE_BUSINESS_NO_LONGER_EXISTS,
  GLOBAL_BUSINESSES,
  MERGE_BUSINESSES,
  PATCH_BUSINESS_FROM_IMPORT,
  SET_BUSINESS_IMPORT_MAPPING,
} from "constants/actionTypes";

import { getLivestockSaleId } from "lib/navigation";
import {
  serializeBusiness,
  serializeGlobalBusinesses,
} from "lib/serializers/businesses";

import { serializeBusinessMergeEvents } from "lib/serializers/businessMergeEvents";
import { offlineActionCreator, urlCheckAndAddIdAndAction } from "./lib";
import { URLCreatorProps } from "./types";

export const deleteBusinessNoLongerExists = id => ({
  type: DELETE_BUSINESS_NO_LONGER_EXISTS,
  meta: {
    business_id: id,
  },
});

export const startTemporaryBusinessResolve = (businessId: string) => ({
  type: TEMPORARY_BUSINESS_RESOLVE_STARTED,
  businessId,
});

export const createBusinessFromImport = (payload, externalCode, source) => ({
  type: CREATE_BUSINESS_FROM_IMPORT,
  payload,
  externalCode,
  source,
});

export const patchBusinessFromImport = (
  businessId,
  payload,
  shouldOverrideShortCode,
  shouldImportContactInfo,
) => ({
  type: PATCH_BUSINESS_FROM_IMPORT,
  businessId,
  payload,
  shouldOverrideShortCode,
  shouldImportContactInfo,
});

export const setBusinessImportMapping = (source, externalCode, businessId) => ({
  type: SET_BUSINESS_IMPORT_MAPPING,
  source,
  externalCode,
  businessId,
});

// Note - we're explicitly looking for `saleyardNameFilter` rather than the globally set `saleyardName` from the URL
const urlCreator = (props: URLCreatorProps = {}) => {
  const { id, action, changesSince, saleyardNameFilter } = props;
  return queryString.stringifyUrl({
    url: urlCheckAndAddIdAndAction("/v2/businesses/", id, action),
    query: {
      changesSince,
      saleyard__name: saleyardNameFilter,
    },
  });
};

export const BusinessAction = offlineActionCreator(
  BUSINESS,
  serializeBusiness,
  urlCreator,
  false,
);

// A Business update that isn't applied until the server confirms it, so a
// failed request leaves the Business as it was.
export const updateBusinessOnSuccess = (
  ...args: Parameters<typeof BusinessAction.update>
) => {
  const action = BusinessAction.update(...args);
  // Without a request to store it, a timestamp would make the success ignored.
  const { REDUX_OFFLINE_TIMESTAMP: ignored, ...commit } =
    action.meta.offline.commit;
  return {
    ...action,
    type: UPDATE_BUSINESS_ON_SUCCESS,
    meta: {
      ...action.meta,
      offline: { ...action.meta.offline, commit },
    },
  };
};

// Inject the current sale id into the create payload.
// A bit nasty, but means changes to the generic create are not lost.
const originalCreateAction = BusinessAction.create;

BusinessAction.create = (
  deserializedPayload,
  options = {
    suppressToast: false,
    suppressErrorToast: false,
    suppressSuccessToast: false,
    changeReason: null,
    saleyardName: null,
  },
) => {
  const livestockSaleId = getLivestockSaleId();
  const extendedPayload = {
    ...deserializedPayload,
    addedInLivestockSaleId:
      deserializedPayload.addedInLivestockSaleId ?? livestockSaleId,
  };
  return originalCreateAction(extendedPayload, options);
};

BusinessAction.suggestBuyerWayProperty = (
  businessId,
  buyerWayName,
  propertyId,
) => ({
  type: BUSINESS.SUGGEST_BUYER_WAY_PROPERTY.ACTION,
  businessId,
  buyerWayName,
  propertyId,
});

BusinessAction.getBusinessesForAutoComplete = (
  term,
  userRole,
  options = {},
) => ({
  type: BUSINESS.GET_BUSINESSES_FOR_AUTO_COMPLETE.REQUEST,
  term,
  userRole,
  options,
});

BusinessAction.getGlobalBusinessesForAutoComplete = (term, options = {}) => ({
  type: BUSINESS.GET_GLOBAL_BUSINESSES_FOR_AUTO_COMPLETE.REQUEST,
  term,
  options,
});

// mergeSourceBusinessId: once the local copy exists, merge that Business
// into it (instead of opening the copy for editing).
BusinessAction.getOrCreateGlobalBusiness = (
  globalBusinessId: string,
  options: { mergeSourceBusinessId?: string; changeReason?: string } = {},
) => ({
  type: BUSINESS.GET_OR_CREATE_GLOBAL_BUSINESS.REQUEST,
  meta: {
    offline: {
      effect: {
        url: urlCreator({
          id: globalBusinessId,
          action: "get-or-create-from-global",
        }),
        method: "POST",
        body: JSON.stringify({}),
      },
      commit: {
        type: BUSINESS.GET_OR_CREATE_GLOBAL_BUSINESS.SUCCESS,
        id: globalBusinessId,
        suppressSuccessToast: true,
        ...options,
      },
      rollback: {
        type: BUSINESS.GET_OR_CREATE_GLOBAL_BUSINESS.FAILURE,
        mergeSourceBusinessId: options.mergeSourceBusinessId,
      },
    },
  },
});

export const GlobalBusinessesAction = offlineActionCreator(
  GLOBAL_BUSINESSES,
  serializeGlobalBusinesses,
  () => "/v2/global-businesses/",
  false,
);

export const MergeBusinessesAction = offlineActionCreator(
  MERGE_BUSINESSES,
  serializeBusinessMergeEvents,
  ({ targetBusinessId, sourceBusinessId }) =>
    `/v2/businesses/${targetBusinessId}/merge/${sourceBusinessId}/`,
  false,
);
